import { faArrowTrendDown, faCircleCheck } from "@fortawesome/free-solid-svg-icons";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { usePermissionModal } from "contexts/PermissionModalContext";
import { formatCount, formatPercentage } from "utils/formatters";
import { DashCard, DashCardHeader, DashCardBody } from "./v2/DashCard";
import DashEmptyState from "./v2/DashEmptyState";

interface ProdutoGiroCaindo {
  descricao: string;
  quantidadeAtual: number;
  quantidadeAnterior: number;
  quedaPercentual: number;
}

interface ObterGiroCaindoResponse {
  produtos: ProdutoGiroCaindo[];
}

const renderPlaceholderRows = () =>
  Array(5)
    .fill(0)
    .map((_, index) => (
      <tr key={index}>
        <td className="dash-skeleton" style={{ height: 14 }} />
        <td className="dash-skeleton" style={{ height: 14 }} />
        <td className="dash-skeleton" style={{ height: 14 }} />
        <td className="dash-skeleton" style={{ height: 14 }} />
      </tr>
    ));

const ProdutosGiroCaindo = () => {
  const { getQueryRetryHandler } = usePermissionModal();

  const { data, isLoading, error } = useQuery({
    queryKey: ["produtosGiroCaindo"],
    queryFn: async () =>
      (await axios.get<ObterGiroCaindoResponse>("/api/dashboard/vendas/giro-caindo")).data,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: getQueryRetryHandler({
      queryKey: ["produtosGiroCaindo"],
      defaultMessage: "Você não possui permissão para visualizar os produtos com giro caindo.",
      showModal: false,
    }),
  });

  const isAccessDenied = axios.isAxiosError(error) && error?.response?.status === 403;
  if (isAccessDenied) return null;

  const produtos = data?.produtos ?? [];

  return (
    <DashCard hoverable={false}>
      <DashCardHeader
        icon={faArrowTrendDown}
        variant="critical"
        title="Produtos com Giro Caindo"
        tooltip="Compara a quantidade vendida nos últimos 30 dias com os 30 dias anteriores. Só considera produtos com 5+ vendas no período anterior, pra evitar ruído de item vendido raramente."
      />
      <DashCardBody flush>
        {isLoading ? (
          <table className="dash-table">
            <tbody>{renderPlaceholderRows()}</tbody>
          </table>
        ) : produtos.length === 0 ? (
          <DashEmptyState icon={faCircleCheck} title="Nenhum produto com queda relevante" description="O giro dos produtos está estável." />
        ) : (
          <table className="dash-table">
            <thead>
              <tr>
                <th>Produto</th>
                <th className="dash-num">Qtd Atual</th>
                <th className="dash-num">Qtd Anterior</th>
                <th className="dash-num">Queda</th>
              </tr>
            </thead>
            <tbody>
              {produtos.map((produto, index) => (
                <tr key={index}>
                  <td className="dash-table-primary">{produto.descricao}</td>
                  <td className="dash-num">{formatCount(produto.quantidadeAtual)}</td>
                  <td className="dash-num">{formatCount(produto.quantidadeAnterior)}</td>
                  <td className="dash-num">
                    <span className={`dash-pill ${produto.quedaPercentual >= 50 ? "dash-pill--critical" : "dash-pill--warning"}`}>
                      -{formatPercentage(produto.quedaPercentual)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </DashCardBody>
    </DashCard>
  );
};

export default ProdutosGiroCaindo;
